import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import {
  ChangeDetectionStrategy,
  Component,
  AfterContentInit,
  Injector,
  forwardRef,
  input,
  computed
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  NgControl,
  ControlValueAccessor,
  Validator,
  AbstractControl,
  ValidationErrors,
  FormControl,
  ReactiveFormsModule,
  NG_VALIDATORS
} from '@angular/forms';
import { BaseControlValueAccessor } from '../base-control-value-accessor';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
  selector: 'lib-form-phone',
  templateUrl: './form-phone.component.html',
  styleUrls: ['./form-phone.component.scss', '../form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    NgxMaskDirective
  ],
  providers: [
    provideNgxMask(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormPhoneComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FormPhoneComponent),
      multi: true
    }
  ]
})
export class FormPhoneComponent extends BaseControlValueAccessor<string> implements AfterContentInit, ControlValueAccessor, Validator {
  label = input<string>('Phone Number');
  startHint = input<string>();
  endHint = input<string>();
  additionalClass = input<string>();
  autocomplete = input<string>('tel');
  pattern = input<string | RegExp>('');
  patternErrorMessage = input<string>();
  readonly = input<boolean>(false);
  alignment = input<'left' | 'center' | 'right'>('left');
  placeholder = input<string>();
  control!: FormControl;

  // Computed signals for phone-specific formatting
  maskComputed = computed<string>(() => '(999) 999-9999');
  patternComputed = computed<string | RegExp>(() => {
    const basePattern = this.pattern();
    return basePattern || /^\(\d{3}\) \d{3}-\d{4}$/;
  });
  patternErrorMessageComputed = computed<string>(() => {
    return this.patternErrorMessage() ?? 'Must be a valid phone number (e.g., (123) 456-7890)';
  });


  constructor(private injector: Injector) {
    super();
  }

  ngAfterContentInit(): void {
    const ngControl: NgControl = this.injector.get(NgControl, null) as NgControl;
    if (ngControl) {
      this.control = ngControl.control as FormControl;
    } else {
      this.control = new FormControl();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return control.valid ? null : control.errors;
  }

  onKeyPress(event: KeyboardEvent): boolean {
    // Allow only numeric input
    const charCode = event.charCode || event.keyCode;
    const char = String.fromCharCode(charCode);
    if (!/^[0-9]$/.test(char)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  get invalid(): boolean | null {
    return this.control ? this.control.invalid : false;
  }

  get showError(): boolean | null {
    if (!this.control) {
      return false;
    }
    const { dirty, touched } = this.control;
    return this.invalid ? dirty || touched : false;
  }
}